import React, { useState, useMemo, useContext } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Paper,
  Stack,
  Button,
} from "@mui/material";
import * as XLSX from "xlsx";
import { AuthContext } from "../context/AuthContext";

const CommonTable = ({
  columns,
  rows,
  rowsPerPageOptions = [10, 25, 100],
  hideSearch,
  downloadFileName = "jass_erp_download",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);

  const { user } = useContext(AuthContext);

  const handleRequestSort = (colField) => {
    const isAsc = orderBy === colField && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(colField);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPage(0);
  };

  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter((row) =>
      columns.some((col) => {
        const val = col.getSearchValue
          ? col.getSearchValue(row)
          : col.field != null
          ? row[col.field]
          : "";
        return String(val ?? "")
          .toLowerCase()
          .includes(term);
      })
    );
  }, [rows, columns, searchTerm]);

  const sortedRows = useMemo(() => {
    if (!orderBy) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      const col = columns.find((c) => c.field === orderBy);
      const aVal = col?.getSortValue ? col.getSortValue(a) : a[orderBy];
      const bVal = col?.getSortValue ? col.getSortValue(b) : b[orderBy];

      if (aVal === undefined || bVal === undefined) return 0;

      if (typeof aVal === "string" && typeof bVal === "string") {
        return order === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (typeof aVal === "number" && typeof bVal === "number") {
        return order === "asc" ? aVal - bVal : bVal - aVal;
      }

      return order === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filteredRows, orderBy, order, columns]);

  const paginatedRows = useMemo(() => {
    return sortedRows.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [sortedRows, page, rowsPerPage]);

  const visibleColumns = useMemo(
    () => columns.filter((col) => !col.exportOnly),
    [columns]
  );

  const handleDownloadExcel = () => {
    const exportData = sortedRows.map((row) => {
      const rowData = {};
      columns.forEach((col) => {
        let value = "";

        if (col.getExportValue) {
          value = col.getExportValue(row);
        } else if (col.renderCell) {
          const cell = col.renderCell(row);
          value =
            typeof cell === "string" || typeof cell === "number"
              ? cell
              : React.isValidElement(cell)
              ? "" // fallback for React components
              : JSON.stringify(cell);
        } else if (col.field) {
          value = row[col.field];
        }

        rowData[col.label] = value ?? "";
      });
      return rowData;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const now = new Date();
    const dateStr = now.toLocaleDateString("en-GB").replace(/\//g, "-");
    const timeStr = now
      .toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
      .replace(":", "-");
    const fullFileName = `${downloadFileName}_${dateStr}_${timeStr}.xlsx`;

    XLSX.writeFile(workbook, fullFileName);
  };

  return (
    <Paper variant="outlined" sx={{ borderColor: "divider" }}>
      {(!hideSearch ||
        user?.role === "super_admin" ||
        user?.role === "admin") && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          p={1}
        >
          {!hideSearch && (
            <TextField
              size="small"
              label="Search"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              sx={{ width: 250 }}
            />
          )}
          {(user?.role === "super_admin" || user?.role === "admin") && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleDownloadExcel}
            >
              Download Excel
            </Button>
          )}
        </Stack>
      )}
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table stickyHeader size="small" sx={{ tableLayout: "auto" }}>
          <TableHead>
            <TableRow>
              {visibleColumns.map((col) => (
                <TableCell
                  key={col.label}
                  align="left"
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "grey.100",
                    py: 1,
                    px: 2,
                  }}
                >
                  {col.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === col.field}
                      direction={orderBy === col.field ? order : "asc"}
                      onClick={() => handleRequestSort(col.field)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map((row) => (
              <TableRow key={row._id} hover>
                {visibleColumns.map((col) => (
                  <TableCell
                    key={col.label}
                    align="left"
                    sx={{
                      py: 1,
                      px: 2,
                      whiteSpace: "pre-line",
                      ...(col.flex ? { width: "31%", maxWidth: "100%" } : {}),
                    }}
                  >
                    {col.renderCell
                      ? col.renderCell(row) ?? "-"
                      : row[col.field] ?? "-"}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={sortedRows.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={rowsPerPageOptions}
      />
    </Paper>
  );
};

export default CommonTable;
